import CloseIcon from "@mui/icons-material/Close";
import {
    Checkbox,
    Chip,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Snackbar,
    Typography,
} from "@mui/material";
import { Box } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import React from "react";
import { useDispatch } from "react-redux";

import { resetUpdateAllowedWorkCentersForShifts } from "../../../../store/gat-admin-slice";

interface AllowedLocationsProps {
    loading: boolean;
    selectedLocations: {
        id: string;
        name: string;
    }[];
    handleLocationChange: (event: SelectChangeEvent<string[]>) => void;
    handleDelete: (event: React.MouseEvent, locationToDelete: string) => void;
    locations: {
        id: string;
        name: string;
    }[];
    updateStatus: string;
}

const AllowedLocations = ({
                              loading,
                              selectedLocations,
                              handleLocationChange,
                              handleDelete,
                              locations,
                          }: AllowedLocationsProps) => {
    return (
        <Box
            sx={{
                marginTop: "8px",
                marginBottom: "8px",
                marginLeft: "8px",
                width: "100%",
            }}
        >
            <Box>
                <Typography
                    sx={{
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "16px",
                        lineHeight: "24px",
                        letterSpacing: "0.15px",
                    }}
                >
                    Applicable Locations
                </Typography>
                <Typography
                    sx={{
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "12px",
                        color: "rgba(0, 0, 0, 0.6)",
                    }}
                >
                    Select the Locations to enable Absence Management in Wilya
                </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl sx={{ width: "100%", marginTop: 2 }}>
                    <InputLabel id='location-select-label'>
                        Select Locations
                    </InputLabel>
                    <Select
                        input={<OutlinedInput label='Select Locations' />}
                        multiple
                        value={selectedLocations?.map((location => location.id)) ?? []}
                        onChange={handleLocationChange}
                        disabled={loading}
                        renderValue={(selected) => {
                            if (selected.length === 0) {
                                return "Select Locations";
                            }
                            if (
                                selected.length === locations?.length
                                && locations.length > 5
                            ) {
                                return "All Locations Selected";
                            }
                            return (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip
                                            key={`allowed-location-${value}`}
                                            label={locations.find(loc => loc.id === value)?.name}
                                            deleteIcon={
                                                <Box
                                                    onMouseDown={(event) => event.stopPropagation()}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        width: "20px",
                                                        height: "20px",
                                                        backgroundColor: "#aeaeae",
                                                        borderRadius: "50%",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <CloseIcon
                                                        sx={{
                                                            color: "#ebebeb",
                                                            fontSize: "16px",
                                                        }}
                                                    />
                                                </Box>
                                            }
                                            onDelete={(event) => handleDelete(event, value)}
                                        />
                                    ))}
                                </Box>
                            );
                        }}
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    maxHeight: 300,
                                },
                            },
                        }}
                    >
                        <MenuItem value='all'>
                            <Checkbox
                                checked={
                                    selectedLocations.length === locations?.length
                                }
                                indeterminate={
                                    selectedLocations.length > 0 &&
                                    selectedLocations.length < locations?.length
                                }
                            />
                            <ListItemText primary='Select All' />
                        </MenuItem>
                        {locations?.map(
                            (location: { id: string; name: string }) => (
                                <MenuItem key={location.id} value={location.id}>
                                    <Checkbox
                                        checked={selectedLocations.findIndex(selectedLocation =>
                                            selectedLocation.id === location.id ) !== -1}
                                    />
                                    <ListItemText primary={location.name} />
                                </MenuItem>
                            )
                        )}
                    </Select>
                </FormControl>
            </Box>
        </Box>
    );
};

export default AllowedLocations;
